import React, { useState, useEffect } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { CircularProgress } from '@mui/material';
import { PillButton, sa } from '../../../components/superAdminUi';
import WizardShell from '../../../components/common/WizardShell';
import SchoolDuplicateWatch from '../../../components/common/SchoolDuplicateWatch';
import { loaderAction } from '../../../store/loaderStore';
import { schoolService } from '../../../services';
import customerTypeService from '../../../services/api/customerTypeService';
import { extractServerErrors } from '../../../utils/serverErrors';
import { fromApiSchool, fromGias } from '../../../utils/schoolFormMapper';
import { buildUpdateSchoolPayload } from './payload';
import SchoolDetailsStep from './SchoolDetailsStep';
import ReviewStep from './ReviewStep';
import SummaryRail from './SummaryRail';

const STEPS = ['School details', 'Review & save'];

// GIAS refresh overwrites a field only when it has a real answer for it; `false` counts,
// and the locked URN is never touched.
const mergeGias = (prev, gias) => {
  const merged = { ...prev };
  Object.keys(gias).forEach((key) => {
    if (key === 'Urn') return;
    const value = gias[key];
    if (value === false) merged[key] = false;
    else if (value !== '' && value !== null && value !== undefined) merged[key] = value;
  });
  return merged;
};

// Full-page school-edit wizard — the same fields EditSchoolModal saved through
// updateSchoolDetailOnly (URN locked, subscription edited separately), now with shared
// wizard chrome and a real review step.
function EditSchool(props) {
  const params = new URLSearchParams(props.location.search);
  const schoolId = parseInt(params.get('schoolId'), 10);

  const [activeStep, setActiveStep] = useState(0);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customerTypes, setCustomerTypes] = useState([]);
  const [duplicate, setDuplicate] = useState(null);
  const [serverErrors, setServerErrors] = useState([]);
  const [giasLoading, setGiasLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    customerTypeService.getAllActiveCustomerTypes().then((response) => {
      if (response.success && response.data) setCustomerTypes(response.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!schoolId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    schoolService.getInfo(schoolId).then((response) => {
      if (response && response.data) {
        setSchool(response.data);
      } else {
        toast.error('Failed to load school details');
      }
      setLoading(false);
    }).catch((error) => {
      toast.error('Error loading school details: ' + (error.message || 'Unknown error'));
      setLoading(false);
    });
  }, [schoolId]);

  const loadFromGias = (formik) => {
    const urn = (formik.values.Urn || '').toString().trim();
    if (!urn) {
      toast.error('Please enter School URN');
      return;
    }
    const urnNumber = parseInt(urn, 10);
    if (isNaN(urnNumber) || urnNumber <= 0) {
      toast.error('Please enter a valid School URN (numeric value)');
      return;
    }
    setGiasLoading(true);
    schoolService.GetGIASSchoolsByURN(urnNumber).then((response) => {
      const schoolData = response?.data ?? (response?.success === undefined ? response : null);
      if (schoolData) {
        formik.setValues(mergeGias(formik.values, fromGias(schoolData)));
        toast.success('School data loaded from GIAS');
      } else {
        toast.error('No school found for this URN');
      }
      setGiasLoading(false);
    }).catch(() => {
      toast.error('Error fetching school details from GIAS');
      setGiasLoading(false);
    });
  };

  const save = (formik) => {
    if (duplicate && duplicate.isDuplicate) {
      toast.error('A school with this name already exists.');
      return;
    }
    setServerErrors([]);
    setSubmitting(true);
    const payload = buildUpdateSchoolPayload(formik.values, school);
    props.loaderActions.dataLoading(true);
    schoolService.updateSchoolDetailOnly(payload).then((response) => {
      props.loaderActions.dataLoading(false);
      setSubmitting(false);
      const { success, message } = response || {};
      if (success) {
        toast.success('School has been updated successfully!');
        setSaved(true);
      } else {
        const errors = extractServerErrors(response);
        if (errors.length) {
          setServerErrors(errors.map((e) => e.message));
        } else {
          toast.error(message || 'Failed to update school');
        }
      }
    }).catch(() => {
      props.loaderActions.dataLoading(false);
      setSubmitting(false);
      setServerErrors(['Failed to update school.']);
    });
  };

  const goToSchool = () => props.history.push(`/school-detail?schoolId=${schoolId}`);

  if (loading) {
    return (
      <WizardShell title="Edit School" steps={STEPS} activeStep={0} hideFooter summary={null}>
        <CircularProgress />
      </WizardShell>
    );
  }

  if (!school) {
    return (
      <WizardShell title="Edit School" steps={STEPS} activeStep={0} hideFooter summary={null}>
        <div>School not found.</div>
      </WizardShell>
    );
  }

  const blocked = Boolean(duplicate && duplicate.isDuplicate);

  return (
    <Formik
      enableReinitialize
      initialValues={fromApiSchool(school)}
      onSubmit={() => {}}
      validateOnBlur={false}
      validateOnChange
      validationSchema={Yup.object().shape({
        Name: Yup.string().trim().max(100, 'School name must be at most 100 characters.').required('School name is required.'),
        Urn: Yup.string()
          .trim()
          .required('School URN is required.')
          .test('is-number', 'School URN must be a valid number', (value) => {
            if (!value) return false;
            const num = parseInt(value, 10);
            return !isNaN(num) && num > 0;
          }),
      })}
    >
      {(formik) => {
        const finalAction = submitting
          ? <CircularProgress size={32} sx={{ color: sa.color.teal }} />
          : <PillButton type="button" $size="lg" onClick={() => save(formik)} disabled={blocked}>Save changes</PillButton>;

        const canLeaveStep = () => {
          if (activeStep === 0) {
            if (!formik.values.Name || formik.values.Name.trim() === '') {
              toast.error('Please enter a school name.');
              return false;
            }
            if (blocked) {
              toast.error('A school with this name already exists.');
              return false;
            }
          }
          return true;
        };

        return (
          <div>
            {/* URN is locked on edit, so the duplicate check keys on the name, excluding
                this school via its own id. */}
            <SchoolDuplicateWatch name={formik.values.Name} urn="" excludeId={schoolId} onResult={setDuplicate} />
            <WizardShell
              title="Edit School"
              steps={STEPS}
              activeStep={activeStep}
              onStepSelect={(index) => setActiveStep(index)}
              hideFooter={saved}
              onBack={() => setActiveStep((s) => s - 1)}
              onNext={() => { if (canLeaveStep()) setActiveStep((s) => s + 1); }}
              finalAction={finalAction}
              summary={<SummaryRail values={formik.values} blocked={blocked} />}
            >
              {activeStep === 0 && (
                <SchoolDetailsStep
                  formik={formik}
                  customerTypes={customerTypes}
                  duplicate={duplicate}
                  serverErrors={serverErrors}
                  onDismissError={(index) => (index < 0 ? setServerErrors([]) : setServerErrors((prev) => prev.filter((_, i) => i !== index)))}
                  giasLoading={giasLoading}
                  onLoadGias={() => loadFromGias(formik)}
                  onEditSubscription={() => props.history.push(`/edit-subscription-school?schoolId=${schoolId}`)}
                />
              )}
              {activeStep === 1 && (
                <ReviewStep values={formik.values} saved={saved} onGoToSchool={goToSchool} />
              )}
            </WizardShell>
          </div>
        );
      }}
    </Formik>
  );
}

const mapDispatchToProps = (dispatch) => ({
  loaderActions: bindActionCreators(loaderAction, dispatch),
});

export default connect(null, mapDispatchToProps)(withRouter(EditSchool));
